import { Dropdown } from 'primereact/dropdown'
import { ProgressSpinner } from 'primereact/progressspinner'
import { FC, useEffect, useMemo, useState } from 'react'
import toast from 'react-hot-toast'
import { useRecoilValue } from 'recoil'
import { useGetHeadlineDataByCases } from '../../../api/report-API'
import { SolveMetricRunTableRow, getSolveMetricRunTable } from '../../../api/solve-metrics-API'
import { useGetSubmission, useGetSubmissions } from '../../../api/submission-API'
import { protoService } from '../../../proto/ProtoService'
import { AxiosResponseProtoData } from '../../../types/types'
import { currentTeamState } from '../../../utils/state-atoms'
import CoefficientCharts from './CoefficientCharts'
import GeometryImages from './GeometryImages'
import ImageComparisonGrid from './ImageComparisonGrid'
import MethodologyConfig from './MethodologyConfig'
import ResultsTable from './ResultsTable'
import RunAttitudes from './RunAttitudes'
import Section from './Section'
import SolverStatistics from './SolverStatistics'
import { HeadlineRecord, mapHeadlineResult, toCaseMeta, toDeltaRows } from './headlineRecords'
import {
  RawRun,
  getRunAttitudes,
  getRunCaseGmIds,
  getRunMethodology,
  toViewModel
} from './methodologyDiff'

// Lightweight projection for the run picker; the full run (with selectedMethodSet) is
// fetched only when a run is chosen.
const RUN_LIST_PROJECTION = JSON.stringify({
  _id: 1,
  name: 1,
  projectName: 1,
  overallStatus: 1,
  timeCreated: 1
})
const RUN_LIST_SORT = JSON.stringify({ timeCreated: -1 })

const decodeRuns = (data?: AxiosResponseProtoData | null): RawRun[] => {
  if (!data?.protoBytesList?.length) return []
  return protoService.decodeProtoResponse(data, 'workflowsubmission') as unknown as RawRun[]
}

// Phase 2 wires these from the selected baseline/proposed runs. Phase 1 renders the
// data-backed section components with empty inputs so each one's layout and empty
// state are in place and ready to receive real data.
type RunOption = { label: string; value: string }

const MethodologyReport: FC = () => {
  const currentTeam = useRecoilValue(currentTeamState)
  const [{ loading: loadingList }, fetchSubmissions] = useGetSubmissions()
  const [{ loading: loadingBaseline }, loadBaselineRun] = useGetSubmission()
  const [{ loading: loadingProposed }, loadProposedRun] = useGetSubmission()

  const [runOptions, setRunOptions] = useState<RunOption[]>([])
  const [baselineRunId, setBaselineRunId] = useState<string | null>(null)
  const [proposedRunId, setProposedRunId] = useState<string | null>(null)
  const [baselineRun, setBaselineRun] = useState<RawRun | null>(null)
  const [proposedRun, setProposedRun] = useState<RawRun | null>(null)
  const [{ loading: loadingHeadline }, getHeadlineByCases] = useGetHeadlineDataByCases()
  const [records, setRecords] = useState<HeadlineRecord[]>([])
  const [solveRows, setSolveRows] = useState<SolveMetricRunTableRow[]>([])

  useEffect(() => {
    let cancelled = false
    setBaselineRunId(null)
    setProposedRunId(null)
    setBaselineRun(null)
    setProposedRun(null)
    fetchSubmissions(`proj=${RUN_LIST_PROJECTION}&skip=0&limit=500&sort=${RUN_LIST_SORT}`)
      .then(res => {
        if (cancelled) return
        const runs = decodeRuns(res.data)
        setRunOptions(
          runs
            .filter(r => !!r._id)
            .map(r => ({
              label: r.projectName ? `${r.name} · ${r.projectName}` : r.name || r._id || '',
              value: r._id || ''
            }))
        )
      })
      .catch(() => {
        if (!cancelled) {
          setRunOptions([])
          toast.error('Failed to load runs.')
        }
      })
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentTeam])

  useEffect(() => {
    if (!baselineRunId) {
      setBaselineRun(null)
      return
    }
    let cancelled = false
    loadBaselineRun(baselineRunId)
      .then(res => !cancelled && setBaselineRun(decodeRuns(res.data)[0] ?? null))
      .catch(() => !cancelled && toast.error('Failed to load the baseline run.'))
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [baselineRunId])

  useEffect(() => {
    if (!proposedRunId) {
      setProposedRun(null)
      return
    }
    let cancelled = false
    loadProposedRun(proposedRunId)
      .then(res => !cancelled && setProposedRun(decodeRuns(res.data)[0] ?? null))
      .catch(() => !cancelled && toast.error('Failed to load the proposed run.'))
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [proposedRunId])

  const baselineVM = useMemo(() => {
    const m = getRunMethodology(baselineRun)
    return m ? toViewModel(m) : null
  }, [baselineRun])
  const proposedVM = useMemo(() => {
    const m = getRunMethodology(proposedRun)
    return m ? toViewModel(m) : null
  }, [proposedRun])

  // Solve-metric run table for the team; matched to runs by run name.
  useEffect(() => {
    if (!currentTeam) return
    let cancelled = false
    getSolveMetricRunTable({ teamScope: { viewAllTeams: false, teams: [currentTeam] } })
      .then(res => !cancelled && setSolveRows(res.tableRows))
      .catch(() => !cancelled && setSolveRows([]))
    return () => {
      cancelled = true
    }
  }, [currentTeam])

  // Headline coefficient data for the proposed run's exact cases (join by gm_id).
  useEffect(() => {
    const gmIds = getRunCaseGmIds(proposedRun)
    if (!gmIds.length) {
      setRecords([])
      return
    }
    let cancelled = false
    getHeadlineByCases({ page: 0, pageSize: 10000, cases: gmIds.join(',') } as any)
      .then((res: any) => !cancelled && setRecords(mapHeadlineResult(res.data)))
      .catch(() => !cancelled && setRecords([]))
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [proposedRun])

  const deltaRows = useMemo(() => toDeltaRows(records), [records])
  const caseMeta = useMemo(() => toCaseMeta(records), [records])
  const baselineSolve = solveRows.find(r => r.run === baselineRun?.name) ?? null
  const proposedSolve = solveRows.find(r => r.run === proposedRun?.name) ?? null
  const attitudeRows = getRunAttitudes(proposedRun ?? baselineRun)
  const proposedRunName = proposedRun?.name
  const loadingRuns = loadingBaseline || loadingProposed || loadingHeadline

  return (
    <div className="flex flex-col gap-4 p-1">
      <div className="rounded-lg border border-amber-300 bg-amber-50 dark:border-amber-700 dark:bg-amber-900/20 px-4 py-2 text-xs text-amber-800 dark:text-amber-200">
        Pick a baseline and proposed run. Each run carries its methodology at
        runConfig.selectedMethodSet (configuration diff), plus attitudes and geometry. Results and
        Charts are joined from headline-data by the run's case gm_ids; Solver statistics come from
        the solve-metric run table; image comparison reuses the post-proc image gallery.
      </div>

      <Section title="Comparison" subtitle="Choose the baseline and proposed run to compare.">
        <div className="flex flex-wrap items-end gap-4">
          <label className="flex flex-col gap-1 text-xs text-gray-600 dark:text-gray-300">
            <span className="font-bold">Baseline run</span>
            <Dropdown
              value={baselineRunId}
              options={runOptions}
              onChange={e => setBaselineRunId(e.value)}
              placeholder="Select baseline run"
              disabled={!runOptions.length}
              filter
              className="w-96"
            />
            {baselineVM && (
              <span
                className="text-[11px] font-semibold text-gm-blue truncate max-w-96"
                title={baselineVM.name}>
                Methodology: {baselineVM.name || '\u2014'}
              </span>
            )}
          </label>
          <label className="flex flex-col gap-1 text-xs text-gray-600 dark:text-gray-300">
            <span className="font-bold">Proposed run</span>
            <Dropdown
              value={proposedRunId}
              options={runOptions}
              onChange={e => setProposedRunId(e.value)}
              placeholder="Select proposed run"
              disabled={!runOptions.length}
              filter
              className="w-96"
            />
            {proposedVM && (
              <span
                className="text-[11px] font-semibold text-gm-blue truncate max-w-96"
                title={proposedVM.name}>
                Methodology: {proposedVM.name || '\u2014'}
              </span>
            )}
          </label>
          {(loadingList || loadingRuns) && (
            <span className="flex items-center gap-2 text-xs text-gray-400 pb-2">
              <ProgressSpinner style={{ width: 16, height: 16 }} strokeWidth="5" /> Loading…
            </span>
          )}
          {!loadingList && !runOptions.length && (
            <span className="text-xs text-gray-400 italic pb-2">No runs found for this team.</span>
          )}
        </div>
      </Section>

      <Section
        title="Methodology configuration"
        subtitle="STAR-CCM+ parameters, hardware, systems and macro steps from each run's methodology.">
        <MethodologyConfig baseline={baselineVM} proposed={proposedVM} />
      </Section>

      <Section title="Results" subtitle="Headline coefficient deltas for the proposed run's cases.">
        <ResultsTable rows={deltaRows} caseMeta={caseMeta} />
      </Section>

      <Section
        title="Solver statistics"
        subtitle="Baseline vs. proposed solve/mesh timing and hardware.">
        <SolverStatistics baseline={baselineSolve} proposed={proposedSolve} />
      </Section>

      <Section
        title="Case attitudes"
        subtitle="Ride heights / yaw / speed per case on the selected run.">
        <RunAttitudes rows={attitudeRows} />
      </Section>

      <Section title="Charts" subtitle="Coefficient deltas vs. attitude for the proposed run.">
        <CoefficientCharts records={records} />
      </Section>

      <Section
        title="Geometry changes"
        subtitle="Geometry-change images attached to the proposed run.">
        <GeometryImages runName={proposedRunName} />
      </Section>

      <Section
        title="Image comparison"
        subtitle="Post-proc images (same source as the Headline Data gallery), baseline vs. proposed by case.">
        <ImageComparisonGrid baselineRun={baselineRun} proposedRun={proposedRun} />
      </Section>
    </div>
  )
}

export default MethodologyReport
